/**
 * Draft carts, parked in localStorage so a lock — manual or the idle safety net —
 * never loses items a server hasn't sent yet.
 *
 * A draft is the *unsent* working cart for one context:
 *   - a table order  → key is the order id, or the table id before an order exists
 *   - the bar tab     → key is the literal string "bar" (one tab in progress per terminal)
 *
 * It is written whenever the cart holds more than what the server already has, and
 * cleared the moment those match (the order was sent) or the cart is emptied. Drafts
 * older than MAX_AGE_MS are treated as gone — yesterday's abandoned tab should not
 * resurface mid-service.
 */

const PREFIX = 'verlo_order_draft_';
const MAX_AGE_MS = 12 * 60 * 60 * 1000; // 12h

const keyFor = (id) => `${PREFIX}${id}`;

export const saveOrderDraft = (id, items) => {
  if (!id) return;
  try {
    if (!items || items.length === 0) {
      localStorage.removeItem(keyFor(id));
      return;
    }
    localStorage.setItem(keyFor(id), JSON.stringify({ savedAt: Date.now(), items }));
  } catch {
    /* private mode / quota — a lost draft is not worth throwing over */
  }
};

export const loadOrderDraft = (id) => {
  if (!id) return null;
  try {
    const raw = localStorage.getItem(keyFor(id));
    if (!raw) return null;
    const { savedAt, items } = JSON.parse(raw);
    const stale = !savedAt || Date.now() - savedAt > MAX_AGE_MS;
    if (stale || !Array.isArray(items) || items.length === 0) {
      localStorage.removeItem(keyFor(id));
      return null;
    }
    return items;
  } catch {
    return null;
  }
};

export const clearOrderDraft = (id) => {
  if (!id) return;
  try {
    localStorage.removeItem(keyFor(id));
  } catch {
    /* ignore */
  }
};

/**
 * A party moved to another table takes its unsent items with it, and leaves none under the
 * table it left for whoever sits there next. Nothing parked under `from`, nothing moves.
 */
export const moveOrderDraft = (from, to) => {
  const items = loadOrderDraft(from);
  if (!items || !to) return;
  saveOrderDraft(to, items);
  clearOrderDraft(from);
};
